import {
  CheckCircleOutline as ApprovedIcon,
  Category as CategoryIcon,
  Cancel as DeclinedIcon,
  Home as HomeIcon,
  Apartment as PropertiesIcon,
  Refresh as RefreshIcon,
  RateReview as ReviewIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';

const StatCard = ({ title, value, subtitle, icon, color, delay = 0 }) => (
  <Fade in={true} timeout={800} style={{ transitionDelay: `${delay}ms` }}>
    <Card
      elevation={0}
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
        border: `1px solid ${color}20`,
        borderRadius: 3,
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 20px 40px ${color}25`,
          border: `1px solid ${color}40`,
          '& .stat-icon': { transform: 'scale(1.1) rotate(5deg)' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
        },
      }}
    >
      <CardContent sx={{ p: 3, pb: '24px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.875rem', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, color: 'text.primary', lineHeight: 1, mb: 0.5 }}
            >
              {value.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              {subtitle}
            </Typography>
          </Box>
          <Avatar
            className="stat-icon"
            sx={{ bgcolor: color, width: 56, height: 56, transition: 'all 0.3s ease', boxShadow: `0 8px 24px ${color}40` }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  </Fade>
);

const AdminDashboard = () => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore.instance;
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalProperties: 0,
    totalCategories: 0,
    propertiesInReview: 0,
    approvedProperties: 0,
    declinedProperties: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const propertiesRef = collection(db, 'properties');

      const [
        totalSnap,
        categoriesSnap,
        reviewSnap,
        approvedSnap,
        declinedSnap,
      ] = await Promise.all([
        getCountFromServer(propertiesRef),
        getCountFromServer(collection(db, 'categories')),
        getCountFromServer(query(propertiesRef, where('approval_status', '==', 'under_review'))),
        getCountFromServer(query(propertiesRef, where('approval_status', '==', 'approved'))),
        getCountFromServer(query(propertiesRef, where('approval_status', '==', 'declined'))),
      ]);

      setStats({
        totalProperties: totalSnap.data().count,
        totalCategories: categoriesSnap.data().count,
        propertiesInReview: reviewSnap.data().count,
        approvedProperties: approvedSnap.data().count,
        declinedProperties: declinedSnap.data().count,
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleNavigateToProperties = (status) => {
    navigate(`/admin/review/${status}`);
  };

  const statsConfig = [
    {
      title: 'إجمالي العقارات',
      value: stats.totalProperties,
      subtitle: 'العدد الكلي للعقارات المسجلة',
      icon: <HomeIcon sx={{ fontSize: 28 }} />,
      color: '#3b82f6',
      delay: 0,
    },
    {
      title: 'الفئات المتاحة',
      value: stats.totalCategories,
      subtitle: 'أنواع العقارات المختلفة',
      icon: <CategoryIcon sx={{ fontSize: 28 }} />,
      color: '#8b5cf6',
      delay: 100,
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>جاري تحميل البيانات...</Typography>
          <Typography variant="body2" color="text.secondary">يتم الآن جلب أحدث الإحصائيات من قاعدة البيانات</Typography>
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} lg={3} key={item}>
              <Card sx={{ height: 160, borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ bgcolor: 'grey.200', height: 20, borderRadius: 1, mb: 1, width: '60%' }} />
                      <Box sx={{ bgcolor: 'grey.200', height: 40, borderRadius: 1, mb: 1, width: '80%' }} />
                      <Box sx={{ bgcolor: 'grey.200', height: 16, borderRadius: 1, width: '50%' }} />
                    </Box>
                    <Avatar sx={{ bgcolor: 'grey.200', width: 56, height: 56 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>تعذر تحميل البيانات</Typography>
          <Typography variant="body2" color="text.secondary">تأكد من اتصالك بالإنترنت وإعدادات قاعدة البيانات</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Fade in={true} timeout={600}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                لوحة تحكم الإدارة
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
                نظرة عامة على إحصائيات النظام والعقارات
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {lastUpdated && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">آخر تحديث</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{lastUpdated.toLocaleTimeString('ar-SA')}</Typography>
                </Box>
              )}
              <Tooltip title="تحديث البيانات" arrow>
                <IconButton
                  onClick={loadStats}
                  disabled={loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.05)' },
                    '&:disabled': { bgcolor: 'grey.300' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {statsConfig.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Property Management Section */}
      <Fade in={true} timeout={800} style={{ transitionDelay: '500ms' }}>
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: '#3b82f6', width: 48, height: 48, mr: 2 }}>
                      <PropertiesIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>عقارات</Typography>
                  </Box>
                  <List sx={{ p: 0 }}>
                    {[
                      { status: 'under-review', label: 'عقارات تحت المراجعة', count: stats.propertiesInReview, icon: <ReviewIcon sx={{ fontSize: 18 }} />, color: '#f59e0b', secondary: 'عقار في انتظار الموافقة' },
                      { status: 'declined', label: 'عقارات مرفوضة', count: stats.declinedProperties, icon: <DeclinedIcon sx={{ fontSize: 18 }} />, color: '#ef4444', secondary: 'عقار تم رفضه' },
                      { status: 'approved', label: 'عقارات موافق عليها', count: stats.approvedProperties, icon: <ApprovedIcon sx={{ fontSize: 18 }} />, color: '#10b981', secondary: 'عقار تمت الموافقة عليه' },
                    ].map(({ status, label, count, icon, color, secondary }) => (
                      <ListItem key={status} sx={{ p: 0, mb: 1 }}>
                        <ListItemButton
                          onClick={() => handleNavigateToProperties(status)}
                          sx={{ borderRadius: 2, border: '1px solid #e5e7eb', '&:hover': { bgcolor: '#f8fafc', borderColor: color } }}
                        >
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>{icon}</Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={label}
                            secondary={`${count} ${secondary}`}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          <Typography variant="h6" sx={{ color, fontWeight: 600, minWidth: 40, textAlign: 'center' }}>
                            {count}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: 3,
                  p: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '200px',
                    height: '200px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    transform: 'translate(50%, -50%)',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>إحصائيات النظام الحالية 📊</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                    يوجد حالياً {stats.activeProperties} عقار نشط من أصل {stats.totalProperties} عقار مسجل في النظام
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    📋 {stats.propertiesInReview} عقار في انتظار المراجعة<br />
                    ✅ {stats.approvedProperties} عقار موافق عليه<br />
                    ❌ {stats.declinedProperties} عقار مرفوض
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Fade>

      {/* Reports placeholder */}
      <Fade in={true} timeout={800} style={{ transitionDelay: '600ms' }}>
        <Box sx={{ mt: 4 }}>
          <Card elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: '#3b82f6', width: 48, height: 48, mx: 'auto', mb: 2 }}>
              <ViewIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>تقارير مفصلة</Typography>
            <Typography variant="body2" color="text.secondary">اطلع على التقارير التفصيلية والإحصائيات المتقدمة</Typography>
          </Card>
        </Box>
      </Fade>
    </Box>
  );
};

export default AdminDashboard;
